// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * Company related projects tab component.
 * Displays projects associated with the company with filtering capabilities.
 */
import { ArchiveFilterControl } from '@/components/archive-filter-control';
import { HardfinTabCardWrapper } from '@/components/hardfin-tab-card-wrapper';
import { TableControlContainer } from '@/components/table-control-container';
import { Sort, invert } from '@/constants/sort';
import { getCustomer } from '@/customer/api/get-customer';
import { CustomerErrorBoundary } from '@/customer/components/customer-error-boundary';
import { useCustomerProjectFilters } from '@/customer/hooks/use-customer-project-filters';
import { ItemFilterMenu } from '@/items/components/item-filter-menu';
import { LocationFilterMenu } from '@/location/components/location-filter-menu';
import { ProjectFilter, ProjectSortProperty } from '@/project/api/get-projects';
import { ProjectStatusFilterMenu } from '@/project/components/project-status-filter-menu';
import { ProjectTableView } from '@/project/components/project-table-view';
import { useProjects } from '@/project/hooks/use-projects';
import { Stack } from '@mui/material';
import { useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';

export const Route = createFileRoute('/_app/companies/$companyKey/projects')({
  component: CompanyProjectsRoute,
});

function CompanyProjectsRoute() {
  const { companyKey } = Route.useParams();

  return (
    <CustomerErrorBoundary>
      <CompanyProjectsContent companyKey={companyKey} />
    </CustomerErrorBoundary>
  );
}

function CompanyProjectsContent(props: { companyKey: string }) {
  const [sort, setSort] = useState<Sort<ProjectSortProperty>>({
    property: 'name',
    direction: 'asc',
  });

  const {
    locationIds,
    productIds,
    statuses,
    isArchived,
    setLocationIds,
    setProductIds,
    setStatuses,
    setIsArchived,
  } = useCustomerProjectFilters();

  const { data } = useSuspenseQuery({
    queryKey: ['customer', props.companyKey],
    queryFn: () => getCustomer(props.companyKey),
  });

  const filter: ProjectFilter = {
    customerIds: [data.customer.id],
    locationIds: locationIds.length > 0 ? locationIds : undefined,
    productIds: productIds.length > 0 ? productIds : undefined,
    statuses: statuses.length > 0 ? statuses : undefined,
    isArchived: isArchived ? 'all' : undefined,
  };

  // Fetch projects for this company with current filters and sort. `data`
  // and `totalCount` are `null` while the current query key is loading;
  // `ProjectTableView` renders a skeleton in that state.
  const {
    data: projects,
    totalCount,
    isLoading,
  } = useProjects({ sort, filter });

  const handleSort = (property: ProjectSortProperty) => {
    setSort((prevSort) => ({
      property,
      direction:
        prevSort.property === property ? invert(prevSort.direction) : 'asc',
    }));
  };

  return (
    <HardfinTabCardWrapper title="Projects">
      <Stack spacing={0}>
        <TableControlContainer>
          <Stack direction="row" marginRight="auto" spacing={1}>
            <LocationFilterMenu value={locationIds} onChange={setLocationIds} />
            <ItemFilterMenu
              label="Products"
              value={productIds}
              type="DEVICE"
              includeArchived={true}
              onChange={setProductIds}
            />
            <ProjectStatusFilterMenu
              label="Status"
              value={statuses}
              onChange={setStatuses}
            />
            <ArchiveFilterControl
              tooltip="Show all project records, including archived projects. Archived projects do not appear by default in the project list."
              isChecked={isArchived}
              onChange={setIsArchived}
            />
          </Stack>
        </TableControlContainer>
        <ProjectTableView
          projects={projects}
          totalCount={totalCount}
          isLoading={isLoading}
          sort={sort}
          onSort={handleSort}
        />
      </Stack>
    </HardfinTabCardWrapper>
  );
}
